import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { AuthService } from "../services";
import { useNavigate, useLocation } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false);
  const refreshTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Process URL tokens only once on mount
  useEffect(() => {
    const processUrlTokens = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get("accessToken");
      const loginTimestamp = urlParams.get("loginTimestamp");

      if (accessToken) {
        const cleanToken = accessToken.startsWith("Bearer ")
          ? accessToken.replace("Bearer ", "")
          : accessToken;
          
        try {
          // Validate token before saving
          const payload = parseJwt(cleanToken);
          const expTime = payload.exp * 1000; // Convert to milliseconds
          
          if (expTime > Date.now()) {
            console.log("Valid token from URL, saving to localStorage");
            localStorage.setItem("accessToken", cleanToken);
            if (loginTimestamp) {
              localStorage.setItem("loginTimestamp", loginTimestamp);
            }
            
            // Extract and save email
            if (payload.email) {
              localStorage.setItem("userEmail", payload.email);
            }
            
            // Remove token from URL to prevent reuse
            navigate(location.pathname, { replace: true });
            
            // Schedule token refresh
            scheduleTokenRefresh(expTime);
          } else {
            console.log("Token from URL is expired, not saving");
            clearAuthData();
          }
        } catch (error) {
          console.error("Error processing URL token:", error);
          clearAuthData();
        }
      }
    };
    
    processUrlTokens();
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("Initializing authentication...");
        
        const token = localStorage.getItem("accessToken");
        if (token) {
          // Validate existing token
          try {
            const payload = parseJwt(token);
            const expTime = payload.exp * 1000;
            
            if (expTime <= Date.now()) {
              console.log("Stored token is expired, attempting refresh");
              await refreshTokenSilently();
            } else {
              console.log("Stored token is valid");
              // Schedule refresh for this token
              scheduleTokenRefresh(expTime);
              
              // Get user data
              await loadUserData();
            }
          } catch (error) {
            console.error("Error validating token:", error);
            clearAuthData();
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        setError(err.message || "Authentication failed");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    
    return () => {
      // Clean up refresh timer on unmount
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);
  
  // Function to parse JWT without external libraries
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error("Invalid token format");
    }
  };
  
  const loadUserData = async () => {
    // Get user data from localStorage or fetch it
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      setCurrentUser(JSON.parse(userDataString));
    } else {
      // If we have a token but no user data, fetch it
      const userEmail = localStorage.getItem("userEmail");
      if (userEmail) {
        try {
          const response = await AuthService.fetchStudentDetails(userEmail);
          if (response.status === "success") {
            setCurrentUser(response.data);
            localStorage.setItem("userData", JSON.stringify(response.data));
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          // Don't clear auth data here, as the token might still be valid
        }
      }
    }
  };
  
  // Schedule token refresh 5 minutes before expiry
  const scheduleTokenRefresh = (expirationTime) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    const timeToRefresh = expirationTime - Date.now() - (5 * 60 * 1000); // 5 min before expiry
    
    if (timeToRefresh <= 0) {
      // Token is already expired or will expire very soon
      refreshTokenSilently();
      return;
    }
    
    console.log(`Scheduling token refresh in ${timeToRefresh/1000} seconds`);
    
    refreshTimerRef.current = setTimeout(() => {
      refreshTokenSilently();
    }, timeToRefresh);
  };
  
  // Silent token refresh
  const refreshTokenSilently = async () => {
    if (isTokenRefreshing) return;
    
    try {
      setIsTokenRefreshing(true);
      const result = await AuthService.refreshToken();
      
      if (result.status === "success") {
        const { accessToken } = result.data;
        const payload = parseJwt(accessToken);
        const expTime = payload.exp * 1000;
        
        scheduleTokenRefresh(expTime);
        
        // No need to reload the page or redirect
        return true;
      }
    } catch (error) {
      console.error("Silent token refresh failed:", error);
      clearAuthData();
      return false;
    } finally {
      setIsTokenRefreshing(false);
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('userData');
    setCurrentUser(null);
    
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  };

  const login = async (emailOrPhoneNumber, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await AuthService.login(emailOrPhoneNumber, password);

      if (response.status === "success") {
        // Store email for future use
        localStorage.setItem("userEmail", emailOrPhoneNumber);
        
        // Set up token refresh
        const { accessToken } = response.data;
        const payload = parseJwt(accessToken);
        const expTime = payload.exp * 1000;
        scheduleTokenRefresh(expTime);

        // Fetch user details
        const userResponse = await AuthService.fetchStudentDetails(
          emailOrPhoneNumber
        );
        if (userResponse.status === "success") {
          setCurrentUser(userResponse.data);
          localStorage.setItem("userData", JSON.stringify(userResponse.data));
        }

        return { success: true };
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (err) {
      setError(err.message || "Login failed");
      return { success: false, error: err.message || "Login failed" };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    AuthService.logout();
    clearAuthData();
  };
  
  const isAuthenticated = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;
    
    try {
      const payload = parseJwt(token);
      return payload.exp * 1000 > Date.now();
    } catch (error) {
      return false;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    isTokenRefreshing,
    refreshTokenSilently,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;